import React, { useState, useEffect } from "react";
import { useModalKeyboardHandlers } from "../../useModalKeyboardHandlers";


export default function FiberMorphologyModal({ initialSelection = {}, onClose, onSave }) {
    const [morphologyData, setMorphologyData] = useState({});

    useEffect(() => {
        const loadMorphologies = async () => {
            try {
                const response = await fetch('/morphology_options.json');
                const data = await response.json();
                // Group by category
                const grouped = (data || []).reduce((acc, m) => {
                    const cat = m.category;
                    if (!acc[cat]) acc[cat] = [];
                    const formattedLabel = m.label.toLowerCase().replace(/[\s\-\/]/g, '_');
                    acc[cat].push({
                        abbreviation: m.abbreviation,
                        label: m.label,
                        // Assuming images are served from public folder
                        img: `/cogni-emcv/fiber/${cat.toLowerCase()}/${cat.toLowerCase()}_${formattedLabel}.png`
                    });
                    return acc;
                }, {});
                setMorphologyData(grouped);
            } catch (err) {
                console.error("Failed to load morphologies:", err);
            }
        };
        loadMorphologies();
    }, []);

    const [selected, setSelected] = useState(initialSelection);

    const toggleSelection = (category, abbreviation) => {
        setSelected((prev) => {
            const prevSelected = prev[category] || [];
            if (prevSelected.includes(abbreviation)) {
                return {
                    ...prev,
                    [category]: prevSelected.filter((x) => x !== abbreviation),
                };
            } else {
                return {
                    ...prev,
                    [category]: [...prevSelected, abbreviation],
                };
            }
        });
    };

    useModalKeyboardHandlers({
        onClose,
        onSave: () => handleSubmit({ preventDefault: () => { } }),
        dependencies: [selected],
    });

    const handleSubmit = (e) => {
        e.preventDefault();

        // Transform the selected map into the array format expected by the backend
        const formattedData = Object.entries(selected).flatMap(([category, abbreviations]) => {
            return abbreviations.map((abbreviation) => {
                const categoryFiles = morphologyData[category] || [];
                const morphItem = categoryFiles.find(item => item.abbreviation === abbreviation);
                const label = morphItem ? morphItem.label : "";

                return {
                    category,
                    abbreviation,
                    label: label
                };
            });
        });

        onSave(formattedData);
        onClose();
    };

    return (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} role="dialog">
            {/* Standard width to match others, scrollable for many images */}
            <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
                <form className="modal-content shadow-lg border-0" onSubmit={handleSubmit}>
                    <div className="modal-header bg-light">
                        <h5 className="modal-title fw-bold text-dark">
                            <i className="bi bi-grid-3x3-gap me-2 text-primary"></i>
                            Select Fiber Morphology
                        </h5>
                        <button type="button" className="btn-close" onClick={onClose} aria-label="Close"></button>
                    </div>

                    <div className="modal-body p-4 bg-light-subtle">
                        {Object.entries(morphologyData).map(([category, types]) => (
                            <div key={category} className="card mb-4 shadow-sm border-light">
                                <div className="card-header bg-white border-bottom-0 pt-3 pb-2">
                                    <h6 className="fw-bold text-secondary text-uppercase mb-0">{category}</h6>
                                </div>
                                <div className="card-body">
                                    <div className="row g-3">
                                        {types.map(({ abbreviation, label, img }) => {
                                            const isSelected = selected[category]?.includes(abbreviation);
                                            return (
                                                <div key={abbreviation} className="col-4 col-sm-3 col-md-4">
                                                    <label
                                                        className={`card h-100 cursor-pointer text-center border-2 transition-all ${isSelected ? 'border-primary bg-primary-subtle' : 'border-light hover-border-secondary'
                                                            }`}
                                                        style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={isSelected || false}
                                                            onChange={() => toggleSelection(category, abbreviation)}
                                                            className="d-none" // Hide default checkbox
                                                        />
                                                        <div className="card-body p-2 d-flex flex-column align-items-center justify-content-center">
                                                            <div className="ratio ratio-1x1 mb-2 w-100">
                                                                <img
                                                                    src={img}
                                                                    alt={label}
                                                                    className="img-fluid rounded object-fit-contain"
                                                                    onError={(e) => { e.target.onerror = null; e.target.src = "https://placehold.co/100x100?text=No+Image"; }} // Fallback
                                                                />
                                                            </div>
                                                            <small className={`fw-bold lh-1 ${isSelected ? 'text-primary' : 'text-dark'}`} style={{ fontSize: '0.9rem' }}>
                                                                {label}
                                                            </small>
                                                        </div>
                                                    </label>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        ))}

                        {Object.keys(morphologyData).length === 0 && (
                            <div className="text-center py-5 text-muted">
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Loading morphology options...
                            </div>
                        )}
                    </div>

                    <div className="modal-footer bg-light border-top-0 py-3">
                        <button type="button" className="btn btn-outline-secondary px-4 rounded-pill fw-bold me-2" onClick={onClose}>
                            Cancel
                        </button>
                        <button type="submit" className="btn btn-primary px-4 shadow-sm fw-bold rounded-pill">
                            <i className="bi bi-check2-circle me-1"></i>
                            Save Selection
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
